import { List, Row } from '@melements/front-components';
import { NotificationsContext, NotificationTypes } from '@melements/front-components/dist/context';
import { FormApi } from 'final-form';
import React, { ChangeEvent, Fragment, useContext, useState } from 'react';
import { Loader, LocaleContext, ModalForm, PageHeader } from 'shared';

import { useQuery } from '../../../shared/hooks';
import { EditTppForm, EditTppPayload, Tpp } from '../../../store/api/tpps/tpps.types';
import { getErrorMessage } from '../../../utils/getErrorMessage/getErrorMessage';
import { PageWrapper, SearchInput, SearchInputWrapper } from '../Dashboard.styles';

import { Service } from './form/fields/service/Service';
import { TppInfo } from './form/fields/tppInfo/TppInfo';
import { COLUMNS } from './table/Table.columns';
import { LeftModalColumn, RightModalColumn, TppsWrapper } from './Tpps.styles';
import { TppsProps } from './Tpps.types';

export const Tpps = ({ onFetchTpps, onEditTpp }: TppsProps) => {
  const localeContext = useContext(LocaleContext);
  const notificationsContext = useContext(NotificationsContext);

  const [editTppModalVisible, setEditTppModalVisible] = useState(false);
  const [selectedTpp, setSelectedTpp] = useState<Tpp>();
  const [search, setSearch] = useState('');
  const { response: tppsResponse, error: tppsError, loading: tppsLoading, handleFetch: tppsHandleFetch } = useQuery(
    onFetchTpps,
  );

  const filteredTpps =
    tppsResponse &&
    tppsResponse.payload.filter(tpp => {
      return (
        tpp.tppName.toLowerCase().includes(search.toLowerCase()) ||
        tpp.tppId.toLowerCase().includes(search.toLowerCase())
      );
    });

  const handleSearch = (event: ChangeEvent<HTMLInputElement>) => {
    setSearch(event.target.value);
  };

  const handleSubmit = async (values: EditTppForm, form: FormApi) => {
    try {
      return await onEditTpp(mapValuesToArray(values), form, () => {
        form.reset();
        setSelectedTpp(undefined);
        setEditTppModalVisible(false);

        showNotificationSuccess();
        tppsHandleFetch();
      });
    } catch (error) {
      showNotificationError(error);
    }
  };

  const showEditTppModal = (tpp: Tpp) => {
    setSelectedTpp(tpp);
    setEditTppModalVisible(true);
  };

  const mapValuesToArray = (values: EditTppForm): EditTppPayload => {
    const services = ['AIS', 'PIS', 'CAF'];
    const blockedServices = services.filter(it => values[it.toLowerCase()] === false);

    return {
      endpointGroups: blockedServices,
      tppId: values.tppId,
    };
  };

  const showNotificationSuccess = () => {
    notificationsContext.showNotification({
      message: localeContext.formatMessage({ id: 'TPPS.FORM.EDIT_SUCCESS' }),
      time: 3000,
      type: NotificationTypes.SUCCESS,
    });
  };

  const showNotificationError = (error: any) => {
    notificationsContext.showNotification({
      message: getErrorMessage(error, localeContext),
      time: 0,
      type: NotificationTypes.ERROR,
    });
  };

  const buildDataForTable = (tppList: Tpp[] | null) => {
    if (tppList) {
      return tppList.map(tpp => {
        return {
          ...tpp,
          ais: tpp.blockedEndpointGroups.includes('AIS'),
          caf: tpp.blockedEndpointGroups.includes('CAF'),
          id: tpp.tppId,
          pis: tpp.blockedEndpointGroups.includes('PIS'),
          ...{ action: showEditTppModal },
        };
      });
    }

    return [];
  };

  const buildInitialDataForModal = (tpp: Tpp) => {
    return {
      ais: !tpp.blockedEndpointGroups.includes('AIS'),
      caf: !tpp.blockedEndpointGroups.includes('CAF'),
      pis: !tpp.blockedEndpointGroups.includes('PIS'),
      tppId: tpp.tppId,
    };
  };

  return (
    <Fragment>
      <PageWrapper>
        <PageHeader>
          {localeContext.formatMessage({ id: 'DASHBOARD.HEADER.TPPS-MANAGEMENT' })}
          <SearchInputWrapper>
            <SearchInput
              placeholder={localeContext.formatMessage({ id: 'GENERAL.SEARCH' })}
              value={search}
              onChange={handleSearch}
              className="reports__search"
            />
          </SearchInputWrapper>
        </PageHeader>
        {tppsLoading && <Loader />}
        {tppsResponse && tppsResponse.payload && !tppsError && (
          <TppsWrapper className="tpps">
            <List
              columns={COLUMNS}
              data={buildDataForTable(filteredTpps)}
              placeholder={<Fragment>{localeContext.formatMessage({ id: 'TPPS.TABLE.EMPTY_LIST' })}</Fragment>}
              loading={false}
              defaultSorting={{ key: 'tppName', descending: false }}
            />
          </TppsWrapper>
        )}
        {selectedTpp && (
          <ModalForm
            initialValues={buildInitialDataForModal(selectedTpp)}
            className="edit-tpp-form"
            name={localeContext.formatMessage({ id: 'TPPS.FORM.EDIT_TPP.NAME' })}
            submitName={localeContext.formatMessage({ id: 'GENERAL.SAVE' })}
            isOpen={editTppModalVisible}
            onClose={setEditTppModalVisible.bind(undefined, false)}
            onSubmit={handleSubmit}
          >
            <Row>
              <LeftModalColumn>
                <Service service="pis" />
                <Service service="ais" />
                <Service service="caf" />
              </LeftModalColumn>
              <RightModalColumn>
                <TppInfo
                  label={localeContext.formatMessage({ id: 'TPPS.FORM.EDIT_TPP.TPP_NAME' })}
                  value={selectedTpp.tppName}
                />
                <TppInfo
                  label={localeContext.formatMessage({ id: 'TPPS.FORM.EDIT_TPP.TPP_ID' })}
                  value={selectedTpp.tppId}
                />
                <TppInfo
                  label={localeContext.formatMessage({ id: 'TPPS.FORM.EDIT_TPP.COUNTRY' })}
                  value={selectedTpp.country}
                />
              </RightModalColumn>
            </Row>
          </ModalForm>
        )}
      </PageWrapper>
    </Fragment>
  );
};
